import { useCallback, useEffect, useRef, useState } from 'react'

import { useHeroContext } from '../hero-provider'
import {
  Centered,
  Slider,
  SliderCompact,
  StaticTransparent,
} from '../hero-variants'

import * as styles from './wrapper.styles'

type CarouselWrapperProps = {
  hideControls?: boolean
}

export const CarouselWrapper = ({ hideControls }: CarouselWrapperProps) => {
  const { currentActiveVariant, slider } = useHeroContext()

  const [currentSlide, setCurrentSlide] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const slideCount = slider?.length || 0
  const duration = 5000
  const showIndicators = !hideControls && slideCount > 1

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount)
    }, duration)
  }, [slideCount])

  const stopAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index)
    startAutoSlide()
  }

  const nextSlide = () => handleSlideChange((currentSlide + 1) % slideCount)
  const prevSlide = () =>
    handleSlideChange((currentSlide - 1 + slideCount) % slideCount)

  useEffect(() => {
    if (slideCount > 1) {
      startAutoSlide()
    }

    return () => stopAutoSlide()
  }, [slideCount, startAutoSlide])

  if (!slider) {
    return null
  }

  return (
    <section css={styles.carousel}>
      <div
        css={styles.carouselInner}
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {slider.map((item, index) => {
          const prioritized = index === 0

          switch (currentActiveVariant) {
            case 'slider':
              return (
                <Slider
                  key={item.id}
                  imagePriority={prioritized}
                  isActive={index === currentSlide}
                  {...item}
                />
              )
            case 'centered':
              return (
                <Centered
                  key={item.id}
                  imagePriority={prioritized}
                  isActive={index === currentSlide}
                  {...item}
                />
              )
            case 'sliderCompact':
              return (
                <SliderCompact
                  key={item.id}
                  currentIndex={currentSlide + 1}
                  handleNext={nextSlide}
                  handlePrev={prevSlide}
                  imagePriority={prioritized}
                  isActive={index === currentSlide}
                  totalSlides={slideCount}
                  {...item}
                />
              )
            case 'staticTransparent':
              return (
                <Slider
                  key={item.id}
                  imagePriority={prioritized}
                  isActive={index === currentSlide}
                  {...item}
                >
                  <StaticTransparent {...item} />
                </Slider>
              )

            default:
              return null
          }
        })}
      </div>
      {showIndicators && (
        <>
          <button
            css={[styles.carouselControl, styles.prevButton]}
            onClick={prevSlide}
          >
            {'\u276E'}
          </button>
          <button
            css={[styles.carouselControl, styles.nextButton]}
            onClick={nextSlide}
          >
            {'\u276F'}
          </button>
          <div css={styles.indicators}>
            {slider.map(({ id }, index) => (
              <button
                key={id}
                css={[
                  styles.indicator(
                    currentActiveVariant === 'staticTransparent'
                  ),
                  index === currentSlide ? styles.activeIndicator : '',
                ]}
                onClick={() => handleSlideChange(index)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  )
}
